import React from 'react';
import { Paper, Tab, Tabs } from '@mui/material';

import AppForm from '@/__core/components/AppForm';
import { usePermissionChecker } from '@/__core/permissions';
import { PERMISSION_RESOURCES } from '@/__core/permissions/utils/PermissionManager';

import {
  getItemTypeOptions,
  InventoryItemFormData,
  InventoryItemOption,
} from '../models/ItemModel';
import ItemInformationTab from './ItemInformationTab';
import ItemIssueHistoryTab from './ItemIssueHistoryTab';
import ItemReceiptHistoryTab from './ItemReceiptHistoryTab';

interface ItemFormProps {
  itemId?: string;
  transactionCompanyId?: string;
  formData: InventoryItemFormData;
  supplierOptions: InventoryItemOption[];
  manufacturerOptions: InventoryItemOption[];
  groupOptions: InventoryItemOption[];
  uomOptions: InventoryItemOption[];
  onChangeForm: (updates: Partial<InventoryItemFormData>) => void;
  onSave: () => void;
  onCancel?: () => void;
  onDelete?: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
  saving?: boolean;
  deleting?: boolean;
  isDirty?: boolean;
  isPersisted?: boolean;
  hideHistoryTabs?: boolean;
}

export default function ItemForm({
  itemId,
  transactionCompanyId,
  formData,
  supplierOptions,
  manufacturerOptions,
  groupOptions,
  uomOptions,
  onChangeForm,
  onSave,
  onCancel = () => {},
  canEdit = true,
  saving = false,
  deleting = false,
  isDirty = false,
  isPersisted = false,
  hideHistoryTabs = false,
}: ItemFormProps) {
  const [activeSection, setActiveSection] = React.useState(0);
  const readOnly = !canEdit;
  const { canCreate } = usePermissionChecker();
  const canCreateSupplier = canCreate(PERMISSION_RESOURCES.SUPPLIERS);
  const itemTypeOptions = getItemTypeOptions(formData.company_id);

  React.useEffect(() => {
    if (isPersisted) return;

    const updates: Partial<InventoryItemFormData> = {};
    if (!formData.group_id && groupOptions.length) updates.group_id = groupOptions[0].id;
    if (!formData.supplier_id && supplierOptions.length) updates.supplier_id = supplierOptions[0].id;
    if (!formData.manufacturer_id && manufacturerOptions.length) updates.manufacturer_id = manufacturerOptions[0].id;
    if (!formData.uom_id && uomOptions.length) updates.uom_id = uomOptions[0].id;
    if (!formData.item_type && itemTypeOptions.length) updates.item_type = itemTypeOptions[0].id;
    if (Object.keys(updates).length) onChangeForm(updates);
  }, [
    formData.group_id,
    formData.supplier_id,
    formData.manufacturer_id,
    formData.uom_id,
    formData.item_type,
    groupOptions,
    supplierOptions,
    manufacturerOptions,
    uomOptions,
    itemTypeOptions,
    onChangeForm,
    isPersisted,
  ]);

  return (
    <AppForm
      onSubmit={onSave}
      onCancel={onCancel}
      submitting={saving}
      submitDisabled={readOnly || saving || deleting || !isDirty}
      submitLabel='Lưu vật tư'
      submittingLabel='Đang lưu...'
      hideActionBar={activeSection !== 0}
      containerSx={{ m: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}
    >
      {!hideHistoryTabs && (
        <Paper sx={{ mb: 2, width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
          <Tabs
            value={activeSection}
            onChange={(_event, value: number) => setActiveSection(value)}
            variant='scrollable'
            scrollButtons={false}
            aria-label='Thông tin và lịch sử kho của vật tư'
            sx={{
              px: 1,
              minHeight: 44,
              borderBottom: '1px solid #e5e7eb',
              '& .MuiTab-root': { minHeight: 44, px: 2.5, fontWeight: 600, textTransform: 'none' },
            }}
          >
            <Tab label='Thông tin' />
            <Tab label='Nhập kho' />
            <Tab label='Xuất kho' />
          </Tabs>
        </Paper>
      )}

      <ItemInformationTab
        formData={formData}
        supplierOptions={supplierOptions}
        manufacturerOptions={manufacturerOptions}
        groupOptions={groupOptions}
        uomOptions={uomOptions}
        onChangeForm={onChangeForm}
        canEdit={canEdit}
        canCreateSupplier={canCreateSupplier}
        active={activeSection === 0}
      />
      {!hideHistoryTabs && (
        <ItemReceiptHistoryTab itemId={itemId} companyId={transactionCompanyId || formData.company_id} active={activeSection === 1} />
      )}
      {!hideHistoryTabs && (
        <ItemIssueHistoryTab itemId={itemId} companyId={transactionCompanyId || formData.company_id} active={activeSection === 2} />
      )}
    </AppForm>
  );
}
